import React from 'react';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { CensoredDataPoint } from '@/utils/weibullCensored';
import { AnalysisMethod } from '@/hooks/useWeibullAnalysis';
import { ColorByOption, MarkerByOption, SizeByOption } from '@/components/analysis/PlotCustomization';
import { WeibullProbabilityPlot, WeibullCDFPlot } from './';
import { PlotCard, ParameterCard, ContentCard } from '../ui';

// Gamma function for MTTF confidence interval calculation
function gamma(z: number): number {
  if (z < 0.5) {
    return Math.PI / (Math.sin(Math.PI * z) * gamma(1 - z));
  }
  z -= 1;
  const x =
    0.99999999999980993 +
    676.5203681218851 / (z + 1) +
    -1259.1392167224028 / (z + 2) +
    771.32342877765313 / (z + 3) +
    -176.61502916214059 / (z + 4) +
    12.507343278686905 / (z + 5) +
    -0.13857109526572012 / (z + 6) +
    9.9843695780195716e-6 / (z + 7) +
    1.5056327351493116e-7 / (z + 8);

  const t = z + 7.5;
  return Math.sqrt(2 * Math.PI) * Math.pow(t, z + 0.5) * Math.exp(-t) * x;
}

interface WeibullAnalysisPanelProps {
  analysis: WeibullAnalysisResult | null;
  isLoading?: boolean;
  hasEnoughData: boolean;
  sampleSize: number;
  showConfidenceIntervals?: boolean;
  confidenceLevel?: number;
  showCensoredData?: boolean;
  metadata?: {
    totalSamples: number;
    failureCount: number;
    censoredCount: number;
    censoringPercentage: number;
    hasCensoredData: boolean;
  } | null;
  censoredData?: CensoredDataPoint[];
  selectedMethod?: AnalysisMethod;
  plotType?: 'probability' | 'cdf';
  showSettingsInSidebar?: boolean;
  colorBy?: ColorByOption;
  markerBy?: MarkerByOption;
  sizeBy?: SizeByOption;
  showLegend?: boolean;
}

const WeibullAnalysisPanel: React.FC<WeibullAnalysisPanelProps> = ({
  analysis,
  isLoading = false,
  hasEnoughData,
  sampleSize,
  showConfidenceIntervals,
  confidenceLevel,
  showCensoredData,
  metadata,
  censoredData,
  selectedMethod: _selectedMethod = 'mle',
  plotType = 'probability',
  showSettingsInSidebar = false,
  colorBy = 'none',
  markerBy = 'none',
  sizeBy = 'uniform',
  showLegend = false,
}) => {

  const plotActions = showSettingsInSidebar ? (
    <div className="flex items-center gap-4 ml-auto">
      {isLoading && (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-onyx-bright-blue"></div>
      )}
    </div>
  ) : null;

  if (!hasEnoughData) {
    return (
      <div className="space-y-6">
        {/* Parameters Cards (Empty State) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ParameterCard
            title="Shape Parameter (β)"
            value="—"
            description="Characterizes failure rate behavior"
            color="bright-blue"
            loading={isLoading}
          />

          <ParameterCard
            title="Scale Parameter (η)"
            value="—"
            description="Characteristic life"
            unit="years"
            color="purple"
            loading={isLoading}
          />

          <ParameterCard
            title="Goodness of Fit (R²)"
            value="—"
            description="Model accuracy"
            color="green"
            loading={isLoading}
          />
        </div>

        {/* Plot Card - Insufficient Data */}
        <ContentCard
          title="Weibull Analytics"
          subtitle="Statistical reliability analysis requires minimum sample size"
          headerBorder={true}
        >
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h3 className="text-lg onyx-subtitle text-gray-900 mb-2">
              Insufficient Data for Analysis
            </h3>
            <p className="text-gray-500 onyx-body mb-2">
              Need at least 3 failure records for Weibull analysis
            </p>
            <div className="text-sm text-gray-400 onyx-body">
              Current sample size: <span className="onyx-subtitle">{sampleSize}</span>
            </div>
            <div className="text-sm text-onyx-bright-blue onyx-subtitle mt-2">
              Adjust filters to include more data
            </div>
          </div>
        </ContentCard>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="space-y-6">
        {/* Parameters Cards (Error State) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ParameterCard
            title="Shape Parameter (β)"
            value="Error"
            description="Unable to calculate"
            color="bright-blue"
            loading={isLoading}
          />

          <ParameterCard
            title="Scale Parameter (η)"
            value="Error"
            description="Unable to calculate"
            unit="years"
            color="purple"
            loading={isLoading}
          />

          <ParameterCard
            title="Goodness of Fit (R²)"
            value="Error"
            description="Unable to calculate"
            color="green"
            loading={isLoading}
          />
        </div>

        {/* Plot Card - Error State */}
        <ContentCard
          title="Weibull Analytics"
          subtitle="Unable to perform statistical analysis on current dataset"
          headerBorder={true}
        >
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 text-red-300">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-lg onyx-subtitle text-red-600 mb-2">Analysis Error</h3>
            <p className="text-gray-500 onyx-body mb-2">Unable to calculate Weibull parameters</p>
            <div className="text-sm text-gray-400 onyx-body">
              Check data quality and try different filters
            </div>
          </div>
        </ContentCard>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Only show old parameter cards if NOT using sidebar settings */}
      {!showSettingsInSidebar && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plotType === 'probability' ? (
          <>
            <ParameterCard
              title="Shape Parameter (β)"
              value={analysis.parameters.shape.toFixed(3)}
              description="Characterizes failure rate behavior"
              confidence={
                analysis.parameters.shapeCI
                  ? {
                      lower: analysis.parameters.shapeCI.lower,
                      upper: analysis.parameters.shapeCI.upper,
                      level: (analysis.statistics?.confidenceLevel || 0.95) * 100,
                    }
                  : undefined
              }
              standardError={analysis.statistics?.standardErrors.shape}
              color="bright-blue"
              loading={isLoading}
            />

            <ParameterCard
              title="Scale Parameter (η)"
              value={analysis.parameters.scale.toFixed(2)}
              description="Characteristic life"
              unit="years"
              confidence={
                analysis.parameters.scaleCI
                  ? {
                      lower: analysis.parameters.scaleCI.lower,
                      upper: analysis.parameters.scaleCI.upper,
                      level: (analysis.statistics?.confidenceLevel || 0.95) * 100,
                    }
                  : undefined
              }
              standardError={analysis.statistics?.standardErrors.scale}
              color="purple"
              loading={isLoading}
            />

            <ParameterCard
              title="Goodness of Fit (R²)"
              value={analysis.parameters.rSquared.toFixed(4)}
              description="Linear regression R² in Weibull coordinates"
              color="green"
              loading={isLoading}
            />
          </>
        ) : (
          <>
            <ParameterCard
              title="Mean Life (MTTF)"
              value={analysis.summary.meanLife.toFixed(2)}
              description="Expected lifetime"
              unit="years"
              confidence={
                analysis.parameters.shapeCI && analysis.parameters.scaleCI
                  ? {
                      lower:
                        analysis.parameters.scaleCI.lower *
                        gamma(1 + 1 / analysis.parameters.shapeCI.upper),
                      upper:
                        analysis.parameters.scaleCI.upper *
                        gamma(1 + 1 / analysis.parameters.shapeCI.lower),
                      level: (analysis.statistics?.confidenceLevel || 0.95) * 100,
                    }
                  : undefined
              }
              standardError="Conservative bounds"
              color="bright-blue"
              loading={isLoading}
            />

            <ParameterCard
              title="Median Life (B50)"
              value={analysis.summary.medianLife.toFixed(2)}
              description="50% failure probability"
              unit="years"
              confidence={
                analysis.parameters.shapeCI && analysis.parameters.scaleCI
                  ? {
                      lower:
                        analysis.parameters.scaleCI.lower *
                        Math.pow(Math.log(2), 1 / analysis.parameters.shapeCI.upper),
                      upper:
                        analysis.parameters.scaleCI.upper *
                        Math.pow(Math.log(2), 1 / analysis.parameters.shapeCI.lower),
                      level: (analysis.statistics?.confidenceLevel || 0.95) * 100,
                    }
                  : undefined
              }
              standardError="Conservative bounds"
              color="purple"
              loading={isLoading}
            />

            <ParameterCard
              title="Characteristic Life (B63.2)"
              value={analysis.summary.characteristicLife.toFixed(2)}
              description="63.2% failure probability"
              unit="years"
              confidence={
                analysis.parameters.scaleCI
                  ? {
                      lower: analysis.parameters.scaleCI.lower,
                      upper: analysis.parameters.scaleCI.upper,
                      level: (analysis.statistics?.confidenceLevel || 0.95) * 100,
                    }
                  : undefined
              }
              standardError="Direct from scale CI"
              color="green"
              loading={isLoading}
            />
          </>
        )}
        </div>
      )}


      {/* Weibull Plots */}
      <PlotCard
        title={plotType === 'probability' ? 'Weibull Probability Plot' : 'Cumulative Distribution Function'}
        subtitle={plotType === 'probability' ? 'Linearized view for assessing fit quality' : 'Probability of failure over time'}
        actions={plotActions}
        loading={isLoading}
      >
        {plotType === 'probability' && (
          <WeibullProbabilityPlot
            analysis={analysis}
            showConfidenceIntervals={showConfidenceIntervals}
            confidenceLevel={confidenceLevel}
            showCensoredData={showCensoredData}
            metadata={metadata}
            censoredData={censoredData}
            colorBy={colorBy}
            markerBy={markerBy}
            sizeBy={sizeBy}
            showLegend={showLegend}
          />
        )}

        {plotType === 'cdf' && (
          <WeibullCDFPlot
            analysis={analysis}
            title="Cumulative Distribution Function (CDF)"
            showConfidenceIntervals={showConfidenceIntervals}
            confidenceLevel={confidenceLevel}
            colorBy={colorBy}
            markerBy={markerBy}
            sizeBy={sizeBy}
            showLegend={showLegend}
          />
        )}
      </PlotCard>
    </div>
  );
};

export default WeibullAnalysisPanel;
